import { useSelector } from 'react-redux';
import { Box, TableSortLabel, Typography } from '@mui/material';
import { type FC, useState } from 'react';
import { MultiselectFilter } from '../multi-select-filter';
import { MenuDateFilter } from '../physician-date-filter';
import { physicianTableHeadersObject } from '../table-heads';
import { OptionType, SortOptions } from '../types/SortOptions';
import {
  PhysicianDTO,
  initialPhysicianDTO,
} from 'src/services/apiServices/physician-service/physician-service';
import {
  documentTypesForPhysician,
  physicianStatus,
} from 'src/pages/therapist-dashboard/therapist-document/document-manegment-const';

type HeaderContentProps = {
  physicianDto: PhysicianDTO;
  setPhysicianDto: (physicianDto: PhysicianDTO) => void;
  item: physicianTableHeadersObject;
  onSort?: (sortOption: SortOptions) => void;
  getAllFacilityList: (label: string) => OptionType[];
  getAllPatient: (label: string) => OptionType[];
  getTheDates: (startDate: Date | null, endDate: Date | null) => { startDate: Date | null; endDate: Date | null };
  handleCallNextApi: (label: string, searchValue: string, isFromSearch: boolean) => void;
  handleCallTheList: (label: string) => void;
  handleSortMultiselect: (label: string, direction: string) => void;
  selectedDueDate: { startDate: Date | null; endDate: Date | null };
  setSelectedDueDate: (date: { startDate: Date | null; endDate: Date | null }) => void;
  facilitiesCount: number,
  patientsCount: number,
};

export const HeaderContent: FC<HeaderContentProps> = ({
  physicianDto,
  setPhysicianDto,
  item,
  onSort,
  getAllFacilityList,
  getAllPatient,
  getTheDates,
  handleCallNextApi,
  handleCallTheList,
  handleSortMultiselect,
  selectedDueDate,
  setSelectedDueDate,
  facilitiesCount,
  patientsCount,
  ...filterProps
}) => {
  const getOptions = (label: string): OptionType[] => {
    if (label === 'Facility Name' || label === 'Patient') {
      return getAllFacilityList(label);
    }
    if (label === 'Status') {
      return physicianStatus;
    }
    return documentTypesForPhysician;
  };

  const renderFilter = () => {
    if (['Days Remaining', 'DOS'].includes(item.label) || !item.filterable) return null;

    switch (item.filterType) {
      case 'date':
        return (
          <MenuDateFilter
            label={item.label}
            setSelectedDueDate={setSelectedDueDate}
            selectedDueDate={selectedDueDate}
          />
        );

      case 'multiselect':
        return (
          <MultiselectFilter
            label={item.label}
            appliedValues={
              item.label === 'Facility Name'
                ? physicianDto.facilityIds
                : item.label === 'Patient'
                ? physicianDto.patientIds
                : item.label === 'Document Type'
                ? physicianDto.type
                : item.label === 'Status'
                ? physicianDto.status
                : []
            }
            setAppliedValues={setPhysicianDto}
            options={getOptions(item.label)}
            handleSortMultiselect={handleSortMultiselect}
            handleCallTheList={handleCallTheList}
            handleCallNextApi={handleCallNextApi}
            forDynamicOption={
              item.label === 'Document Type' || item.label === 'Status' ? true : false
            }
            sortConfig={physicianDto}
            totalElements={
              item.label === 'Facility Name'
                ? facilitiesCount
                : item.label === 'Patient'
                ? patientsCount
                : 0
            }
          />
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {item.sortable ? (
        <TableSortLabel
          active={physicianDto.sortBy === item.value}
          direction={physicianDto.sortDirection}
          onClick={() =>
            item.sortable &&
            onSort &&
            onSort({ key: item.value, direction: physicianDto.sortDirection })
          }
        >
          <Typography variant="subtitle2">{item.label}</Typography>
        </TableSortLabel>
      ) : (
        <Typography variant="subtitle2">{item.label}</Typography>
      )}
      {renderFilter()}
    </Box>
  );
};
